import {process, State} from "@progress/kendo-data-query"
import {Button} from "@progress/kendo-react-buttons"
import {ExcelExport, ExcelExportColumn} from "@progress/kendo-react-excel-export"
import {Grid, GridCellProps, GridColumn, GridDataStateChangeEvent, GridToolbar} from "@progress/kendo-react-grid"
import {useInternationalization} from "@progress/kendo-react-intl"
import {Tooltip} from "@progress/kendo-react-tooltip"
import {useEffect, useRef, useState} from "react"
import {DealerReport} from "../../../../containers/PassRate/passRateReportSlice"
import {RootState} from "../../../../rootReducer"
import {columnProps} from "../../../../shared/ActiveGridFilter"
import {SpinnerGrid} from "../../../../shared/CommonItems"
import {useAppSelector} from "../../../../store"
import {processExportData} from "../../../../utils/common"

const initialState: State = {skip: 0, take: 50}

const DealerReportGrid = () => {
    const intl = useInternationalization()

    const _export = useRef<ExcelExport | null>(null)
    const [dataState, setDataState] = useState(initialState)

    const dealersReport = useAppSelector((state: RootState) => state.containers.passRateReport.prDealersReport)
    const isDealerReportLoading = useAppSelector(
        (state: RootState) => state.containers.passRateReport.prDealersReportLoader,
    )
    useEffect(() => {
        setDataState(initialState)
    }, [dealersReport])

    const CustomColumnCell = (props: GridCellProps) => (
        <td title={props.dataItem[`${props.field}`]}>{props.dataItem[`${props.field}`]}</td>
    )

    const GridHeaderCell = (props: any, subtitle: string) => (
        <a>
            <span title={Object.keys(subtitle).length === 0 ? props.title : subtitle}>{props.title}</span>
            {props.children}
        </a>
    )

    const exportExcelData = (dealersReport: DealerReport[]) =>
        dealersReport!.map(({...item}) => ({
            ...item,
        }))

    return (
        <div className="position-relative">
            {isDealerReportLoading && <SpinnerGrid />}
            <Tooltip openDelay={100} position="bottom" anchorElement="pointer">
                <ExcelExport
                    fileName={`Dealer-${intl.formatDate(new Date(), "yyyy-MM-ddTHH-mm-ss")}`}
                    data={exportExcelData(processExportData(dealersReport, dataState))}
                    ref={_export}
                >
                    <ExcelExportColumn field="regionName" title="Region" />
                    <ExcelExportColumn field="preparerDealerName" title="Prepared by Dealer" />
                    <ExcelExportColumn field="preparerID" title="PreparerID" />
                    <ExcelExportColumn field="tests" title="Tests" />
                    <ExcelExportColumn field="pass" title="PASS" />
                    <ExcelExportColumn field="fail" title="FAIL" />
                    <ExcelExportColumn field="passRate" title="FTP %" cellOptions={{format: "0.00\\%"}} width={60}/>
                </ExcelExport>
                <Grid
                    style={{
                        height: "calc(100vh -  17.1rem)",
                    }}
                    data={process(dealersReport, dataState)}
                    {...dataState}
                    pageable
                    sortable
                    resizable
                    onDataStateChange={(e: GridDataStateChangeEvent) => setDataState(e.dataState)}
                >
                    <GridToolbar>
                        <Button
                            fillMode={"flat"}
                            themeColor="primary"
                            className="buttons-container-button"
                            icon="excel"
                            disabled={process(dealersReport, dataState).total === 0}
                            onClick={() => _export.current !== null && _export.current.save()}
                        >
                            Export to Excel
                        </Button>
                    </GridToolbar>
                    <GridColumn
                        {...columnProps("regionName", dataState)}
                        field="regionName"
                        title="Region"
                        cell={CustomColumnCell}
                        width={185}
                    />
                    <GridColumn
                        {...columnProps("preparerDealerName", dataState)}
                        field="preparerDealerName"
                        title="Prepared by Dealer"
                        cell={CustomColumnCell}
                        width={160}
                        headerCell={props => GridHeaderCell(props, "Prepared by Dealer")}
                    />
                    <GridColumn
                        {...columnProps("preparerID", dataState)}
                        field="preparerID"
                        title="PreparerID"
                        cell={CustomColumnCell}
                        width={160}
                    />
                    <GridColumn
                        field="tests"
                        title="Tests"
                        width={"100px"}
                        headerCell={props =>
                            GridHeaderCell(props, "MOTs reported by DVSA that contribute towards FTP %")
                        }
                    />

                    <GridColumn
                        field="pass"
                        title="PASS"
                        width={"100px"}
                        headerCell={props =>
                            GridHeaderCell(props, "MOTs that didn't fail in the last 3 months from the booking date")
                        }
                    />
                    <GridColumn
                        field="fail"
                        title="FAIL"
                        width={"100px"}
                        headerCell={props =>
                            GridHeaderCell(props, "MOTs reported by DVSA along with tests failed in the last 3 months")
                        }
                    />
                    <GridColumn
                        field="passRate"
                        title="FTP %"
                        cell={(props: any) => (
                            <td className="">{intl.formatNumber(props.dataItem.passRate, "0.00\\%")}</td>
                        )}
                        headerCell={props => GridHeaderCell(props, "Evaluated by comparing PASS from TotalTests")}
                        width={120}
                    />
                </Grid>
            </Tooltip>
        </div>
    )
}

export default DealerReportGrid
